import { MONTHS } from './constants';
import { showErrorMessage } from './utils';
import { ModalService } from '../services/modal.service';
import { I18nTranslator } from '../i18n/translate';

export function isDayBeyondMonthEnd(daysInMonth: number[], monthIndex: number, day: number): boolean {
  return day > daysInMonth[monthIndex];
}

export function getWeekDates(
  weekNumber: number,
  months: string[],
  campaignYear: number,
): Array<{ date: Date; day: number; month: string }> {
  if (weekNumber < 1 || weekNumber > 52) {
    return [];
  }
  const year = campaignYear;
  const firstDayOfYear = new Date(Date.UTC(year, 0, 1));
  const daysOffset = (weekNumber - 1) * 7;
  const weekStartDate = new Date(firstDayOfYear.getTime());
  weekStartDate.setUTCDate(firstDayOfYear.getUTCDate() + daysOffset);

  const dayOfWeek = weekStartDate.getUTCDay();
  const offset = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
  weekStartDate.setUTCDate(weekStartDate.getUTCDate() - offset);

  const dates: Array<{ date: Date; day: number; month: string }> = [];
  for (let i = 0; i < 7; i++) {
    const currentDate = new Date(weekStartDate.getTime());
    currentDate.setUTCDate(weekStartDate.getUTCDate() + i);
    dates.push({
      date: currentDate,
      day: currentDate.getUTCDate(),
      month: months[currentDate.getUTCMonth()],
    });
  }
  return dates;
}

export function getDateFromMonthDay(months: string[], month: string, day: number, campaignYear: number) {
  const monthIndex = months.indexOf(month);
  if (monthIndex !== -1) {
    const year = campaignYear;
    return new Date(year, monthIndex, day);
  }
  return null;
}

export function getDateFromMonthDay2024(months: string[], month: string, day: number) {
  const monthIndex = months.indexOf(month);
  if (monthIndex !== -1) {
    const year = 2024;
    return new Date(year, monthIndex, day);
  }
  return null;
}

export function handleErroWithMsg(error: any, msg: string, modal: ModalService, i18nTranslator: I18nTranslator) {
  if (error.status === 401) {
    showErrorMessage(modal, i18nTranslator.translate('planning.error.unauthorized'));
  } else {
    console.error(error);

    let errorMessage = msg;
    try {
      const errorBody = JSON.parse(error._body);
      if (errorBody && errorBody.errorMessage) {
        errorMessage = errorBody.errorMessage;
      }
    } catch (e) {
      console.warn('Error parsing error:', e);
    }
    showErrorMessage(modal, errorMessage);
  }
}

export function isLeapYear(year: number): boolean {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
}

export function parseDateKey(month: string, day: number): string {
  return `${month}-${day}`;
}

export function initializeDaysInMonth(currentYear: number): number[] {
  const daysInMonth = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  if (isLeapYear(currentYear)) {
    daysInMonth[1] = 29;
  }
  return daysInMonth;
}

export interface WeekInMonthInfo {
  startDate: Date;
  endDate: Date;
  startDay: number;
  endDay: number;
  month: string;
}

/**
 * Returns the start/end date info for a given 1-based week number in the current year.
 * If the week number is out of range, returns null.
 */
export function getWeekDaysInMonthRange(weekNumber: number, year: number, months: string[]): WeekInMonthInfo {
  if (weekNumber < 1 || weekNumber > 52) {
    return null;
  }
  const firstDayOfYear = new Date(year, 0, 1);
  const daysOffset = (weekNumber - 1) * 7;
  const weekStartDate = new Date(firstDayOfYear.getTime());
  weekStartDate.setDate(firstDayOfYear.getDate() + daysOffset);

  const dayOfWeek = weekStartDate.getDay();
  weekStartDate.setDate(weekStartDate.getDate() - dayOfWeek);

  const weekEndDate = new Date(weekStartDate.getTime());
  weekEndDate.setDate(weekStartDate.getDate() + 6);

  return {
    startDate: weekStartDate,
    endDate: weekEndDate,
    startDay: weekStartDate.getDate(),
    endDay: weekEndDate.getDate(),
    month: months[weekStartDate.getMonth()],
  };
}
